import API from './index';
import AppUrls from '../config/AppUrls';
import { buildPathWithParams } from '../utils/Paths';
import { apiCredentialTypes } from '../utils/Constants';

class MerchantsService {
  static fetchCountries() {
    return API.get(AppUrls.api.merchantsCountries).then(
      (response) => response.data,
      () => []
    );
  }

  static fetchMerchant(merchantId) {
    const url = buildPathWithParams(AppUrls.api.merchant, { merchantId });
    return API.get(url).then((response) => response.data);
  }

  static fetchCountriesCurrency() {
    return API.get(AppUrls.api.merchantsCountriesCurrency).then(
      (response) => response.data,
      () => []
    );
  }

  static fetchChargebackFeeData(idMerchant, params = {}) {
    const url = buildPathWithParams(AppUrls.api.merchantChargebackFeeData, {
      idMerchant,
    });
    return API.get(url, params).then((response) => response.data);
  }

  static fetchMerchantsAll(params = {}) {
    return API.get(AppUrls.api.merchants, params).then((response) => response.data);
  }

  static fetchMerchantsAllByText(text, isId, params = {}, filterByWallet) {
    const endpoint = isId
      ? `${AppUrls.api.merchants}?id=${text}`
      : `${AppUrls.api.merchants}?param=${text}`;

    return API.get(endpoint, params).then((response) => {
      let merchants = response.data;

      // filterByWallet
      if (filterByWallet === true) {
        merchants = merchants.filter((merchant) => merchant.hasWallet === true);
      } else if (filterByWallet === false) {
        merchants = merchants.filter((merchant) => merchant.hasWallet === false);
      }

      return merchants;
    });
  }

  static fetchReferredMerchantsAllByText(text, _isId, { mainMerchantId }) {
    if (mainMerchantId) {
      return API.get(`${AppUrls.api.subMerchantsList}?name=${text}&mainMerchantId=${mainMerchantId}`).then((response) => response.data);
    }
    return API.get(`${AppUrls.api.subMerchantsList}?name=${text}`).then((response) => response.data);
  }

  static fetchMainMerchantsAllByText(text) {
    return API.get(`${AppUrls.api.mainMerchantsList}?name=${text}`).then((response) => response.data);
  }

  static fetchAllActiveMerchantsByText(text, isId, params = {}) {
    const newParams = {
      ...params,
      all: false,
    };
    if (isId) {
      return API.get(`${AppUrls.api.merchants}?id=${text}`, newParams).then((response) => response.data);
    }
    return API.get(`${AppUrls.api.merchants}?param=${text}`, newParams).then((response) => response.data);
  }

  static fetchMerchantsList(filters) {
    return API.get(AppUrls.api.merchantsList, filters).then((response) => response.data);
  }

  static fetchReferredMerchantsList(filters) {
    return API.get(AppUrls.api.referredMerchantsList, filters).then((response) => response);
  }

  static fetchMerchantDetails() {
    return API.get(AppUrls.api.merchantDetails).then((response) => response.data);
  }

  static fetchMerchantDetailsCompany(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigCompany, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchCountryChangeRegistry(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.countryChangeRegistry, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsCompany(idMerchant, company) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigCompany, {
      idMerchant,
    });
    return API.post(url, company).then((response) => response.data);
  }

  static updateChargebackFeeData(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.merchantChargebackFeeData, {
      idMerchant,
    });
    return API.post(url, data).then((response) => response.data);
  }

  static addMerchantImage(idMerchant, file, type = 'logo') {
    const url = buildPathWithParams(type === 'logo' ? AppUrls.api.merchantAddLogo : AppUrls.api.merchantAddPaymentPageLogo, { idMerchant });
    return API.post(url, { file }).then((response) => response.data);
  }

  static removeMerchantLogo(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantRemoveLogo, {
      idMerchant,
    });
    return API.delete(url).then((response) => response.data);
  }

  static fetchMerchantDetailsFinancial(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFinancial, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static getIsLocalBankEnabled(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantLocalBank, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsFinancial(idMerchant, financial) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFinancial, {
      idMerchant,
    });
    return API.post(url, financial).then((response) => response.data);
  }

  static deleteMerchantDetailsFinancial(idMerchant, idBank) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFinancial, {
      idMerchant,
    });
    return API.delete(`${url}/${idBank}`).then((response) => response.data);
  }

  static requestBankDetails(idMerchant) {
    return API.post(buildPathWithParams(AppUrls.api.merchantConfigBankTicket, { idMerchant })).then((response) => response.data);
  }

  static fetchMerchantDetailsContacts(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigContacts, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsContacts(idMerchant, contacts) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigContacts, {
      idMerchant,
    });
    return API.post(url, contacts).then((response) => response.data);
  }

  static fetchMerchantDetailsIntegration(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigIntegration, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsIntegration(idMerchant, integration) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigIntegration, {
      idMerchant,
    });
    return API.post(url, integration).then((response) => response.data);
  }

  static updateMerchantDetailsIntegrationViewSecret(idMerchant, body) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigIntegrationSecretKey, { idMerchant });
    return API.post(url, body).then((response) => response.data);
  }

  static fetchMerchantDetailsConfigFee(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFee, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigFee(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFee, {
      idMerchant,
    });
    return API.post(url, config).then((response) => response.data);
  }

  static updateMerchantDetailsConfigFeeByPaymentType(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFeeByPaymentType, {
      idMerchant,
    });
    return API.put(url, null, config).then((response) => response.data);
  }

  static fetchMerchantDetailsConfigTier(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigTier, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigTier(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigTier, {
      idMerchant,
    });
    return API.post(url, config).then((response) => response.data);
  }

  static fetchMerchantDetailsConfigFx(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFx, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigFx(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFxEdit, {
      idMerchant,
    });
    return API.post(url, config).then((response) => response.data);
  }

  static updateMerchantDetailsConfigFeeAmountRange = (values, idMerchant) => {
    const url = buildPathWithParams(AppUrls.api.merchantConfigFeeAmountRange, {
      idMerchant,
    });
    return API.post(url, values).then((response) => response.data);
  };

  static editMerchantDetailsConfigFeeAmountRange = (values, idMerchant, idMerchantsFeeAmountRange) => {
    const path = buildPathWithParams(AppUrls.api.merchantConfigFeeAmountRangeEdit, { idMerchant, idMerchantsFeeAmountRange });
    return API.put(path, values);
  };

  static fetchMerchantDetailsConfigGeneral(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigGeneral, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantDetailsProcessingEntities(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantProcessingEntities, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantDetailsCurrencies(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantPossibleCurrencies, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigGeneral(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigGeneralEdit, {
      idMerchant,
    });
    return API.post(url, config).then((response) => response.data);
  }

  static fetchMerchantDetailsFeeCurrencies(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantFeeCurrencies, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigCurrency(idMerchant, currency) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigEditCurrency, {
      idMerchant,
      currency,
    });
    return API.post(url).then((response) => response.data);
  }

  static updateMerchantDetailsConfigGeneralLegal(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigGeneralLegal, {
      idMerchant,
    });
    return API.post(url, config).then((response) => response.data);
  }

  static updateMerchantDetailsConfigGeneralCommercial(idMerchant, config) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigGeneralCommercial, { idMerchant });
    return API.post(url, config).then((response) => response.data);
  }

  static fetchMerchantCashinLimits(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantCashinLimits, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantCashoutLimits(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantCashoutLimits, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantCashinLimit(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.merchantCashinLimits, {
      idMerchant,
    });
    return API.post(url, data).then((response) => response.data);
  }

  static updateMerchantCashoutLimit(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.merchantCashoutLimits, {
      idMerchant,
    });
    return API.post(url, data).then((response) => response.data);
  }

  static fetchMerchantCashinCountries(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantCashinCountries, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantCashoutFee(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantCashoutFee, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantFeeAmountRange(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantFeeAmountRange, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantCashoutStatus(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantCashoutStatus, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static fetchMerchantMaintenanceFee(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantMaintenanceFee, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantCashoutFee(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.merchantCashoutFee, {
      idMerchant,
    });
    return API.post(url, data).then((response) => response.data);
  }

  static updateMerchantCashoutStatus(idMerchant, status) {
    const url = buildPathWithParams(AppUrls.api.merchantActivateCashout, {
      idMerchant,
      status,
    });
    return API.post(url).then((response) => response.data);
  }

  static updateMerchantMaintenanceFee(idMerchant, maintenanceFee) {
    const url = buildPathWithParams(AppUrls.api.merchantConfigMaintenanceFee, {
      idMerchant,
      maintenanceFee,
    });
    return API.post(url).then((response) => response.data);
  }

  static saveMerchantMailGroup(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.merchantMailGroups, {
      idMerchant,
    });
    return API.post(url, data).then((response) => response.data);
  }

  static fetchMerchantMailGroups(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantMailGroups, {
      idMerchant,
    });
    return API.get(url).then((response) => response.data);
  }

  static removeMerchantMailGroups(idMerchant, idGroup) {
    const url = buildPathWithParams(AppUrls.api.merchantRemoveMailGroup, {
      idMerchant,
      idGroup,
    });
    return API.delete(url).then((response) => response.data);
  }

  static fetchScheduledSettlements(idMerchant) {
    const path = buildPathWithParams(AppUrls.api.scheduledSettlements, {
      idMerchant,
    });
    return API.get(path).then((response) => response.data);
  }

  static updateScheduledSettlement(idMerchant, data) {
    const path = buildPathWithParams(AppUrls.api.scheduledSettlements, {
      idMerchant,
    });
    return API.post(path, data).then((response) => response.data);
  }

  static fetchMerchantIps(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantIps, { idMerchant });
    return API.get(url).then((response) => response.data);
  }

  static updateMerchantIps(idMerchant, body) {
    const url = buildPathWithParams(AppUrls.api.merchantIps, { idMerchant });
    return API.post(url, body).then((response) => response.data);
  }

  static fetchRollingReserveData(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.rollingReserveConfig, { idMerchant });
    return API.get(url)
      .then((response) => response.data)
      .catch((error) => {
        console.error('Error fetching rolling reserve data:', error);
      });
  }

  static createRollingReserveData(idMerchant, data) {
    const url = buildPathWithParams(AppUrls.api.rollingReserveCreateConfig, { idMerchant });
    return API.post(url, data)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.error('Error posting rolling reserve data:', error);
      });
  }

  static getMerchantsReferralsFiles(idMerchant) {
    const url = buildPathWithParams(AppUrls.api.merchantsReferralsFiles, { idMerchant });
    return API.get(url).then((response) => response.data);
  }

  static directIpAction(idMerchant, ip, list, action) {
    let url = '';
    switch (action) {
      case 'remove':
        url = list === 'ipAddressCashout' ? AppUrls.api.merchantRemoveCashoutIp : AppUrls.api.merchantRemoveDepositIp;
        url = buildPathWithParams(url, { idMerchant });
        return API.delete(url, { data: { ip } }).then((response) => response.data);
      default:
        url = list === 'ipAddressCashout' ? AppUrls.api.merchantAddCashoutIp : AppUrls.api.merchantAddDepositIp;
        url = buildPathWithParams(url, { idMerchant });
        return API.post(url, { ip }).then((response) => response.data);
    }
  }

  static generateTransKey(type) {
    let url = '';
    switch (type) {
      case apiCredentialTypes.CASHIN:
        url = AppUrls.api.merchantGenerateDepositTransKey;
        break;
      case apiCredentialTypes.CASHOUT:
        url = AppUrls.api.merchantGenerateCashoutTransKey;
        break;
      case apiCredentialTypes.WEB_STATUS:
        url = AppUrls.api.merchantGenerateWebStatusTransKey;
        break;
      default:
        url = '';
        break;
    }

    return url && API.post(url).then((response) => response.data);
  }

  static generateApiKey(type) {
    let url = '';
    switch (type) {
      case apiCredentialTypes.CASHIN:
        url = AppUrls.api.merchantGenerateDepositApiKey;
        break;
      case apiCredentialTypes.CASHOUT:
        url = AppUrls.api.merchantGenerateCashoutApiKey;
        break;
      case apiCredentialTypes.WEB_STATUS:
        url = AppUrls.api.merchantGenerateWebStatusApiKey;
        break;
      default:
        url = '';
        break;
    }

    return url && API.post(url).then((response) => response.data);
  }

  static generateSecretKey(type) {
    let url = '';
    switch (type) {
      case apiCredentialTypes.CASHIN:
        url = AppUrls.api.merchantGenerateDepositSecretKey;
        break;
      case apiCredentialTypes.CASHOUT:
        url = AppUrls.api.merchantGenerateCashoutSecretKey;
        break;
      default:
        url = '';
        break;
    }

    return url && API.post(url).then((response) => response.data);
  }
}
export default MerchantsService;
